import AuthLayout from "@/layouts/AuthLayout";
import logoIcon from "@/assets/Logo.svg";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import useNotification from "@/hooks/use-notification";
import { getUserTypeFromAuthResponse } from "../utils/token.utils";
import { isAdminLoginAllowed } from "@/constants/auth";
import { getRefreshToken, getToken, removeTokens } from "@/utils/auth";
import { authApi } from "../services/auth.api";
import { setUserFromToken, useStore } from "@/store";
import { markLoginAnnouncementsPending } from "@/features/client-announcements/utils/loginAnnouncementPrompt";

import { t } from "i18next";
import StaffLogin from "../components/StaffLogin";
import AdminLogin from "../components/AdminLogin";
import RoleSelection from "../components/RoleSelection";
import StepHeader from "../components/StepHeader";
import styles from "../styles/login.module.scss";
import { LoginStep, LoginRole } from "../constants/loginSteps";
import { getStepContent } from "../utils/loginSteps.utils";
import {
  readStoredLoginRole,
  storeLoginRole,
  stepForLoginRole,
} from "../utils/loginRole.utils";
import { useCompanyInfo } from "../hooks/useCompanyInfo";

const LoginPage = () => {
  // Sekme son seçimden devam eder; yenilemede Personel'e dönüp kullanıcıyı
  // yanlış formda bırakmasın.
  const [selectedRole, setSelectedRole] = useState<LoginRole>(readStoredLoginRole);
  const [step, setStep] = useState<LoginStep>(() =>
    stepForLoginRole(readStoredLoginRole()),
  );
  const navigate = useNavigate();
  const { notifySuccess } = useNotification();
  const { domainID: currentDomainID, domainName: currentDomainName } =
    useCompanyInfo();

  /**
   * Yönetici sekmesi girişi. Asıl kontrol backend'de (portal=ADMIN → yönetici
   * olmayan hesaba token verilmez, yanlış şifreyle aynı 401). Buradaki kontrol
   * yalnızca derinlemesine savunmadır: eski bir backend token döndürürse bile
   * oturum açılmaz, token iptal edilir ve AdminLogin yanlış şifreyle birebir aynı
   * genel hatayı gösterir (false dönüşü).
   */
  const handleAdminLoginSuccess = (response: any): boolean => {
    const userType = getUserTypeFromAuthResponse(response);
    if (userType === null || !isAdminLoginAllowed(userType)) {
      const refreshToken = getRefreshToken();
      const accessToken = getToken() ?? undefined;
      removeTokens();
      if (refreshToken) {
        // Best-effort: yanlışlıkla verilmiş refresh token'ı sunucuda da iptal et.
        void authApi
          .logout(refreshToken, { skipErrorToast: true, accessToken })
          .catch(() => undefined);
      }
      return false;
    }
    setUserFromToken(response, "admin");
    notifySuccess(t("login.loginSuccess"));
    navigate("/dashboard");
    return true;
  };

  /**
   * Kursiyer sekmesinden yapılan giriş tamamlandığında panele yönlendirir.
   *
   * Panel StaffLogin içinde "client" olarak yazıldı; burada
   * yalnızca kursiyer panelinde beklenen duyuru popup'ını işaretliyoruz.
   */
  const finishLogin = () => {
    if (!useStore.getState().isStaff) {
      markLoginAnnouncementsPending();
    }
    navigate("/dashboard");
  };


  const handleRoleChange = (role: number) => {
    const nextRole =
      role === LoginRole.MANAGER ? LoginRole.MANAGER : LoginRole.EMPLOYEE;
    setSelectedRole(nextRole);
    storeLoginRole(nextRole);
    setStep(stepForLoginRole(nextRole));
  };

  const shouldShowRoleSelection =
    step === LoginStep.STAFF_LOGIN || step === LoginStep.ADMIN_LOGIN;
  const stepContent = getStepContent(step) as {
    title: string;
    description: string;
  };

  return (
    <AuthLayout>
      <div className={styles.login}>
        <div className={styles["login__form"]}>
          <div className={styles["login__wrapper"]}>
            <div className={styles["login__logo"]}>
              <img src={logoIcon} alt="EUAS" />
            </div>

            <StepHeader
              title={stepContent.title}
              description={stepContent.description}
            />

            {shouldShowRoleSelection && (
              <RoleSelection
                selectedRole={selectedRole}
                onRoleChange={handleRoleChange}
              />
            )}

            {step === LoginStep.ADMIN_LOGIN && (
              <AdminLogin
                currentDomainID={currentDomainID}
                currentDomainName={currentDomainName}
                onLoginSuccess={handleAdminLoginSuccess}
              />
            )}

            {step === LoginStep.STAFF_LOGIN && (
              <StaffLogin
                onLoginSuccess={finishLogin}
                domainID={currentDomainID}
              />
            )}

          </div>
        </div>
      </div>
    </AuthLayout>
  );
};

export default LoginPage;
